export interface ITimeModel {
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
}

function msToTimeModel(ms: number): ITimeModel {
  const totalMilliseconds = Math.floor(ms);
  const hours = Math.floor(totalMilliseconds / 3600000);
  const remainingMilliseconds = totalMilliseconds % 3600000;
  const minutes = Math.floor(remainingMilliseconds / 60000);
  const seconds = Math.floor((remainingMilliseconds % 60000) / 1000);
  const milliseconds = remainingMilliseconds % 1000;
  return {
    hours,
    minutes,
    seconds,
    milliseconds,
  };
}

export class Time {
  private readonly totalMilliseconds: number;

  private constructor(
    public readonly hours = 0,
    public readonly minutes = 0,
    public readonly seconds = 0,
    public readonly milliseconds = 0,
  ) {
    const hoursInMs = hours * 60 * 60 * 1000;
    const minutesInMs = minutes * 60 * 1000;
    const secondsInMs = seconds * 1000;

    this.totalMilliseconds = Math.floor(hoursInMs + minutesInMs + secondsInMs + milliseconds);
  }

  private static checkSanity(timeModel: ITimeModel) {
    return (
      Number.isInteger(timeModel.hours) &&
      Number.isInteger(timeModel.minutes) &&
      timeModel.minutes >= 0 &&
      timeModel.minutes < 60 &&
      Number.isInteger(timeModel.seconds) &&
      timeModel.seconds >= 0 &&
      timeModel.seconds < 60 &&
      Number.isInteger(timeModel.milliseconds) &&
      timeModel.milliseconds < 1000
    );
  }

  private static sanitize(timeModel: ITimeModel): ITimeModel {
    const hoursInMs = timeModel.hours * 60 * 60 * 1000;
    const minutesInMs = timeModel.minutes * 60 * 1000;
    const secondsInMs = timeModel.seconds * 1000;

    const totalMilliseconds = Math.floor(hoursInMs + minutesInMs + secondsInMs + timeModel.milliseconds);
    return msToTimeModel(totalMilliseconds);
  }

  public valueOf() {
    return this.totalMilliseconds;
  }

  public toMilliseconds() {
    return this.totalMilliseconds;
  }

  public toSeconds(): number {
    return this.totalMilliseconds / 1000;
  }

  public toMinutes(): number {
    return this.toSeconds() / 60;
  }

  public toHours(): number {
    return this.toMinutes() / 60;
  }

  public toString(precision: 'hour' | 'minute' | 'second' = 'second') {
    const format = (unit: number) => (unit < 10 ? `0${unit}` : unit.toString());
    if (precision === 'hour') {
      return format(this.hours);
    }
    if (precision === 'minute') {
      return `${format(this.hours)}:${format(this.minutes)}`;
    }
    return `${format(this.hours)}:${format(this.minutes)}:${format(this.seconds)}`;
  }

  public copy() {
    return Time.fromMilliseconds(this.toMilliseconds());
  }

  public static fromMilliseconds(milliseconds: number): Time {
    const timeModel = msToTimeModel(milliseconds);
    return new Time(timeModel.hours, timeModel.minutes, timeModel.seconds, timeModel.milliseconds);
  }

  public static fromObject(object: Partial<ITimeModel>) {
    const timeModel: ITimeModel = {
      hours: object.hours || 0,
      minutes: object.minutes || 0,
      seconds: object.seconds || 0,
      milliseconds: object.milliseconds || 0,
    };
    if (Time.checkSanity(timeModel)) {
      return new Time(timeModel.hours, timeModel.minutes, timeModel.seconds, timeModel.milliseconds);
    }
    const sanitizedTime = Time.sanitize(timeModel);
    return new Time(sanitizedTime.hours, sanitizedTime.minutes, sanitizedTime.seconds, sanitizedTime.milliseconds);
  }

  public static add(t1: Time, t2: Time): Time {
    return Time.fromObject({
      hours: t1.hours + t2.hours,
      minutes: t1.minutes + t2.minutes,
      seconds: t1.seconds + t2.seconds,
      milliseconds: t1.milliseconds + t2.milliseconds,
    });
  }
}
